import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import './UploadZone.css';

function UploadZone({ onFilesSelected, files, isProcessing, onUpload, onReset, hasExistingData, fileStatuses, processedCount }) {
  const [isDragActive, setIsDragActive] = useState(false);

  const onDrop = useCallback((acceptedFiles) => {
    onFilesSelected(acceptedFiles);
    setIsDragActive(false);
  }, [onFilesSelected]);

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
      'image/gif': ['.gif'],
      'image/webp': ['.webp'],
      'image/bmp': ['.bmp'],
      'image/tiff': ['.tiff', '.tif'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-powerpoint': ['.ppt'],
      'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['.pptx']
    },
    maxSize: 20 * 1024 * 1024, // 20MB
    multiple: true,
    onDragEnter: () => setIsDragActive(true),
    onDragLeave: () => setIsDragActive(false)
  });

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <div className="upload-zone-container">
      <div {...getRootProps()} className={`dropzone ${isDragActive ? 'drag-active' : ''} ${files.length > 0 ? 'has-files' : ''}`}>
        <input {...getInputProps()} />

        {files.length === 0 ? (
          <div className="dropzone-empty">
            <div className="upload-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="17 8 12 3 7 8" />
                <line x1="12" y1="3" x2="12" y2="15" />
              </svg>
            </div>
            <h3 className="dropzone-title">
              {hasExistingData ? 'Ajouter d\'autres documents' : 'Glissez vos documents ici'}
            </h3>
            <p className="dropzone-subtitle">
              {hasExistingData ? (
                <>
                  Les nouveaux documents seront ajoutés aux existants
                  <br />
                  <span className="size-info">PDF, images, Word, Excel, PowerPoint • Max: 20 MB/fichier</span>
                </>
              ) : (
                <>
                  PDF, images, Word, Excel, PowerPoint • Dépôt en bloc possible
                  <br />
                  <span className="size-info">Taille maximale: 20 MB par fichier, 200 MB par lot</span>
                </>
              )}
            </p>
            <button className="browse-button">
              {hasExistingData ? 'Ajouter des fichiers' : 'Parcourir les fichiers'}
            </button>
          </div>
        ) : (
          <div className="files-list">
            <div className="files-header">
              <h3>{files.length} fichier{files.length > 1 ? 's' : ''} sélectionné{files.length > 1 ? 's' : ''}</h3>
              {!isProcessing && (
                <button className="clear-button" onClick={(e) => { e.stopPropagation(); onReset(); }}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                  Effacer
                </button>
              )}
            </div>

            <div className="files-items">
              {files.map((file, index) => {
                const status = fileStatuses[index] || 'pending';
                return (
                  <div key={index} className={`file-item file-item-${status}`}>
                    <div className={`file-icon file-icon-${status}`}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" />
                        <polyline points="13 2 13 9 20 9" />
                      </svg>
                    </div>
                    <div className="file-info">
                      <div className="file-name">{file.name}</div>
                      <div className="file-size">{formatFileSize(file.size)}</div>
                    </div>
                    <div className="file-status">
                      {status === 'processing' && (
                        <div className="spinner"></div>
                      )}
                      {status === 'success' && (
                        <div className="status-success">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                            <polyline points="20 6 9 17 4 12" />
                          </svg>
                        </div>
                      )}
                      {status === 'error' && (
                        <div className="status-error">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                            <line x1="18" y1="6" x2="6" y2="18" />
                            <line x1="6" y1="6" x2="18" y2="18" />
                          </svg>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {files.length > 0 && !isProcessing && (
        <div className="upload-actions">
          <button className="upload-button" onClick={onUpload}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="20 6 9 17 4 12" />
            </svg>
            {hasExistingData ? `Ajouter ${files.length} document${files.length > 1 ? 's' : ''}` : `Traiter les documents (${files.length})`}
          </button>
        </div>
      )}

      {isProcessing && (
        <div className="processing-banner">
          <div className="processing-spinner"></div>
          <div className="processing-text">
            <strong>Extraction en cours...</strong>
            <span>Claude Vision analyse vos documents</span>
          </div>
          <div className="processing-progress">
            <span className="progress-count">{processedCount}/{files.length}</span>
            <span className="progress-label">documents traités</span>
          </div>
        </div>
      )}
    </div>
  );
}

export default UploadZone;
